/**
 * ArtifactConfigRenderer - Output node artifact configuration
 *
 * Handles artifact type selection and dynamically shows artifact-specific
 * fields from the type's data_schema for user to map from input fields.
 *
 * Uses the standard factory pattern for field mapping (see workflow-field-mapping-design.md).
 */

import React, { useMemo } from 'react';
import { Input } from '@/components/ui/Input';
import { FieldSourceToggle } from '../FieldSourceToggle';
import { useArtifactTypes } from '@/features/system-settings/hooks/useSystemConfig';
import { useI18n } from '@/i18n';
import type { BaseCustomRendererProps } from './types';
import { configFieldKey } from './types';
import type { ArtifactFieldDef } from '@/types/system';
import { isMediaField, getMediaCategory } from '@/types/system';
import type { FieldType } from '@/types/workflowFields';

export const ArtifactConfigRenderer: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  onBatchUpdate,
  fieldSourceHandlersFactory,
  availableSources = [],
  customInputs = [],
  contextVariables = [],
  startVariables = [],
}) => {
  const { data: artifactTypes = [], isLoading: artifactTypesLoading } = useArtifactTypes();
  const { language, t } = useI18n();

  // Get selected artifact type config
  const selectedArtifactType = (nodeData.artifact_type as string) || '';
  const selectedTypeConfig = useMemo(() => {
    return artifactTypes.find(t => t.id === selectedArtifactType)?.config;
  }, [artifactTypes, selectedArtifactType]);

  // Get data schema fields for the selected artifact type
  const dataSchemaFields = useMemo(() => {
    return (selectedTypeConfig?.data_schema || []) as ArtifactFieldDef[];
  }, [selectedTypeConfig]);

  const handleUpdate = (field: string, value: unknown) => {
    if (onBatchUpdate) {
      onBatchUpdate({ [field]: value });
    }
  };

  // Convert artifact field content_types to workflow field type
  const getFieldTypeFromContentTypes = (field: ArtifactFieldDef): FieldType => {
    if (!isMediaField(field)) {
      return 'string'; // Metadata fields are string by default
    }
    const category = getMediaCategory(field.content_types!);
    switch (category) {
      case 'image': return 'image';
      case 'audio': return 'audio';
      case 'video': return 'video';
      case 'document': return 'document';
      default: return 'string';
    }
  };

  // Get placeholder text based on field type
  const getPlaceholder = (field: ArtifactFieldDef): string => {
    if (isMediaField(field)) {
      const category = getMediaCategory(field.content_types!);
      const mediaLabels: Record<string, string> = {
        'image': t('imageWorkflows.artifactConfig.mapImagePath'),
        'audio': t('imageWorkflows.artifactConfig.mapAudioPath'),
        'video': t('imageWorkflows.artifactConfig.mapVideoPath'),
        'document': t('imageWorkflows.artifactConfig.mapDocumentPath'),
      };
      return mediaLabels[category || ''] || t('imageWorkflows.artifactConfig.mapMediaPath');
    }
    return t('imageWorkflows.artifactConfig.enterFieldLabel').replace('{label}', getFieldLabel(field));
  };

  // Get label based on language
  const getFieldLabel = (field: ArtifactFieldDef): string => {
    if (language === 'zh' && field.label_zh) {
      return field.label_zh;
    }
    return field.label || field.key;
  };

  return (
    <div className="space-y-4">
      {/* Save to Library Toggle */}
      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
        <label className="text-sm font-medium text-gray-700">{t('imageWorkflows.artifactConfig.saveToLibrary')}</label>
        <input
          type="checkbox"
          checked={(nodeData.save_to_library as boolean) || false}
          onChange={(e) => handleUpdate('save_to_library', e.target.checked)}
          disabled={isDisabled}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </div>

      {nodeData.save_to_library && (
        <div className="space-y-3 pl-3 border-l-2 border-green-200">
          {/* Library Image Source - must be mapped */}
          <FieldSourceToggle
            fieldKey={configFieldKey('library_image')}
            label={t('imageWorkflows.artifactConfig.imageSource')}
            description={t('imageWorkflows.artifactConfig.imageSourceDescription')}
            required={true}
            availableSources={availableSources}
            customInputs={customInputs}
            contextVariables={contextVariables}
            startVariables={startVariables}
            mapping={fieldSourceHandlersFactory?.createFieldHandlers(configFieldKey('library_image'))?.mapping}
            onMappingChange={fieldSourceHandlersFactory?.createFieldHandlers(configFieldKey('library_image'))?.onMappingChange || (() => {})}
            onKeyPathChange={fieldSourceHandlersFactory?.createFieldHandlers(configFieldKey('library_image'))?.onKeyPathChange || (() => {})}
            onMappingRemove={fieldSourceHandlersFactory?.createFieldHandlers(configFieldKey('library_image'))?.onMappingRemove || (() => {})}
            disabled={isDisabled}
            compatibleTypes={['image', 'image_url']}
          >
            <Input
              value={(nodeData.library_image as string) || ''}
              onChange={(e) => handleUpdate('library_image', e.target.value)}
              placeholder={t('imageWorkflows.artifactConfig.imageSourcePlaceholder')}
              disabled={isDisabled}
            />
          </FieldSourceToggle>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('imageWorkflows.artifactConfig.category')}</label>
            <Input
              value={(nodeData.category as string) || ''}
              onChange={(e) => handleUpdate('category', e.target.value)}
              placeholder={t('imageWorkflows.artifactConfig.categoryPlaceholder')}
              disabled={isDisabled}
            />
            <p className="text-xs text-gray-500 mt-1">{t('imageWorkflows.artifactConfig.variableHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('imageWorkflows.artifactConfig.filename')}</label>
            <Input
              value={(nodeData.filename as string) || ''}
              onChange={(e) => handleUpdate('filename', e.target.value)}
              placeholder={t('imageWorkflows.artifactConfig.filenamePlaceholder')}
              disabled={isDisabled}
            />
            <p className="text-xs text-gray-500 mt-1">{t('imageWorkflows.artifactConfig.optional')}</p>
          </div>
        </div>
      )}

      {/* Artifact Type Selection */}
      <div className="pt-3 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('imageWorkflows.artifactConfig.saveAsArtifact')}
          {artifactTypesLoading && (
            <span className="ml-2 text-gray-400 text-xs">{t('imageWorkflows.artifactConfig.loading')}</span>
          )}
        </label>
        <select
          value={selectedArtifactType}
          onChange={(e) => handleUpdate('artifact_type', e.target.value || undefined)}
          disabled={artifactTypesLoading || isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
        >
          <option value="">{t('imageWorkflows.outputNode.noArtifact')}</option>
          {artifactTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {language === 'zh' && type.config.name_zh
                ? type.config.name_zh
                : type.config.name}{' '}
              ({type.id})
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {t('imageWorkflows.artifactConfig.artifactDescription')}
        </p>
      </div>

      {/* Dynamic Artifact Data Schema Fields */}
      {selectedArtifactType && dataSchemaFields.length > 0 && (
        <div className="space-y-3 pl-3 border-l-2 border-blue-200">
          <div className="text-sm font-medium text-blue-700 mb-2">
            {t('imageWorkflows.artifactConfig.artifactFieldMapping')}
            <span className="font-normal text-blue-500 ml-2">
              ({t('imageWorkflows.artifactConfig.fieldsCount').replace('{count}', String(dataSchemaFields.length))})
            </span>
          </div>

          {dataSchemaFields.map((field) => {
            // Use standard pattern: configFieldKey(field.key) generates __config__image, __config__phrase, etc.
            const fieldKey = configFieldKey(field.key);
            const handlers = fieldSourceHandlersFactory?.createFieldHandlers(fieldKey);
            // Static value stored directly in nodeData[field.key]
            const fieldValue = (nodeData[field.key] as string) || '';
            const isMedia = isMediaField(field);
            const mediaCategory = isMedia ? getMediaCategory(field.content_types!) : null;

            return (
              <FieldSourceToggle
                key={field.key}
                fieldKey={fieldKey}
                label={
                  <span className="flex items-center gap-2">
                    {getFieldLabel(field)}
                    {isMedia && mediaCategory && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                        {mediaCategory}
                      </span>
                    )}
                  </span>
                }
                description={field.description}
                required={field.required}
                availableSources={availableSources}
                customInputs={customInputs}
                contextVariables={contextVariables}
                startVariables={startVariables}
                mapping={handlers?.mapping}
                onMappingChange={handlers?.onMappingChange || (() => {})}
                onKeyPathChange={handlers?.onKeyPathChange || (() => {})}
                onMappingRemove={handlers?.onMappingRemove || (() => {})}
                disabled={isDisabled}
                compatibleTypes={[getFieldTypeFromContentTypes(field)]}
              >
                {/* All fields use text input - metadata fields store values directly, media fields store file paths */}
                <Input
                  value={fieldValue}
                  onChange={(e) => handleUpdate(field.key, e.target.value)}
                  placeholder={getPlaceholder(field)}
                  disabled={isDisabled}
                />
              </FieldSourceToggle>
            );
          })}

          <p className="text-xs text-blue-600 mt-2">
            {t('imageWorkflows.artifactConfig.mappingTip')}
          </p>
        </div>
      )}

      {/* Fallback: Legacy title/description for types without data_schema */}
      {selectedArtifactType && dataSchemaFields.length === 0 && (
        <div className="space-y-3 pl-3 border-l-2 border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('imageWorkflows.artifactConfig.artifactTitle')}</label>
            <Input
              value={(nodeData.artifact_title as string) || ''}
              onChange={(e) => handleUpdate('artifact_title', e.target.value)}
              placeholder={t('imageWorkflows.artifactConfig.artifactTitlePlaceholder')}
              disabled={isDisabled}
            />
            <p className="text-xs text-gray-500 mt-1">{t('imageWorkflows.artifactConfig.variableHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('imageWorkflows.artifactConfig.artifactDescriptionField')}</label>
            <Input
              value={(nodeData.artifact_description as string) || ''}
              onChange={(e) => handleUpdate('artifact_description', e.target.value)}
              placeholder={t('imageWorkflows.artifactConfig.artifactDescriptionPlaceholder')}
              disabled={isDisabled}
            />
            <p className="text-xs text-gray-500 mt-1">{t('imageWorkflows.artifactConfig.optional')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtifactConfigRenderer;
